import { CfsTemplateBaseComponent, CfsTemplateError, CfsTemplateErrorData, TemplateAnimationType } from "clearline-common";
import { Directive, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { BehaviorSubject, combineLatest } from "rxjs";
import { filter, map, take } from "rxjs/operators";
import { DigitalAssetScreenSize } from "clearline-api";
import {
  CfsHtmlTemplateErrorTypes,
  CfsTemplateErrorDataFields,
  CfsTemplateErrorTypes,
  generateCfsHtmlTemplateErrorMessage,
  generateCfsTemplateError
} from "../cfs-template-errors";

@Directive({ standalone: true })
export class HtmlTemplateBaseComponent implements CfsTemplateBaseComponent, OnInit {
  @Input() screenSize: DigitalAssetScreenSize | null = null;
  @Input() animationType: TemplateAnimationType = TemplateAnimationType.Animated;
  @Input() responsive = false;
  @Input() isLowResolutionScreen = false;

  @Output() ready = new EventEmitter<void>();
  @Output() error = new EventEmitter<CfsTemplateError>();

  private readonly fontName = "SF Pro";

  protected animated$ = new BehaviorSubject<boolean>(false);
  protected logoLoaded$ = new BehaviorSubject<boolean>(false);
  protected qrCodeLoaded$ = new BehaviorSubject<boolean>(false);
  protected mainImageLoaded$ = new BehaviorSubject<boolean>(false);
  protected fontLoaded$ = new BehaviorSubject<boolean>(false);

  play(): void {
    this.startAnimation();
  }

  stop(): void {
    this.stopAnimation();
  }

  pause(): void {
    this.stopAnimation();
  }

  restart(): void {
    this.startAnimation();
  }

  ngOnInit(): void {
    combineLatest([this.logoLoaded$, this.qrCodeLoaded$, this.mainImageLoaded$, this.fontLoaded$])
      .pipe(
        map(([logoLoaded, qrCodeLoaded, mainImageLoaded, fontsLoaded]) => logoLoaded && qrCodeLoaded && mainImageLoaded && fontsLoaded),
        filter((contentReady) => contentReady),
        take(1)
      )
      .subscribe(() => {
        this.ready.emit();
      });

    this.checkFontLoaded();

    if (this.animationType === TemplateAnimationType.Animated) {
      this.startAnimation();
    }
  }

  protected setError(errorType: CfsHtmlTemplateErrorTypes | CfsTemplateErrorTypes, dataField = "", dataValue = ""): void {
    const errorData: CfsTemplateErrorData | null = dataField ? { field: dataField, value: dataValue } : null;
    const message: string = generateCfsHtmlTemplateErrorMessage(errorType);
    const templateError: CfsTemplateError = generateCfsTemplateError(errorType, message, errorData);

    this.error.emit(templateError);
  }

  protected onLogoLoaded(): void {
    this.logoLoaded$.next(true);
  }

  protected onLogoError(url = ""): void {
    this.setError(CfsHtmlTemplateErrorTypes.LogoLoad, CfsTemplateErrorDataFields.Url, url);
  }

  protected onQrCodeLoaded(): void {
    this.qrCodeLoaded$.next(true);
  }

  protected onQrCodeError(url = ""): void {
    this.setError(CfsHtmlTemplateErrorTypes.QrCodeLoad, CfsTemplateErrorDataFields.Url, url);
  }

  protected onMainImageLoaded(): void {
    this.mainImageLoaded$.next(true);
  }

  protected onMainImageError(url = ""): void {
    this.setError(CfsHtmlTemplateErrorTypes.MainImageLoad, CfsTemplateErrorDataFields.Url, url);
  }

  // ******************** Private methods ********************

  private startAnimation(): void {
    this.animated$.next(
      this.animationType === TemplateAnimationType.Animated || this.animationType === TemplateAnimationType.OnDemandAnimated
    );
  }

  private stopAnimation(): void {
    this.animated$.next(false);
  }

  private checkFontLoaded(): void {
    document.fonts
      .load(`1em "${this.fontName}"`)
      .then((fonts) => {
        if (fonts.length > 0) {
          this.fontLoaded$.next(true);
        } else {
          this.setError(CfsHtmlTemplateErrorTypes.FontLoad, CfsTemplateErrorDataFields.FontName, this.fontName);
        }
      })
      .catch(() => this.setError(CfsHtmlTemplateErrorTypes.FontLoad, CfsTemplateErrorDataFields.FontName, this.fontName));
  }
}
